/**
 * Reads and writes the per-org Signify mapping configuration at
 * `parameters/signifyMappings`. There are NO code defaults: an org that has not
 * been configured gets an empty `fieldMappings` map and a
 * `configDisabledReason`. A configured org still needs the Record Name
 * destination, because the line-item builder throws when it cannot name a
 * record, so that destination is a second reason to disable. Both reasons read
 * out of `configDisabledReason`, which is the single derivation behind both the
 * disabled Signify menu items and the settings-page warning, so the two can
 * never drift.
 *
 * `save` uses full `set` (never `setMerge`): merge recurses into the nested
 * destination / rule maps and so cannot delete a destination or a rule. `set`
 * requires the store to be connected — the effect below guarantees that for the
 * hook's lifetime.
 *
 * Consumers: `SignifyMappingsSettings`, `useSignifyProductModal`,
 * `useSignifyConfigurationSidebar`, `useCreateLineItemFromSignify`.
 */
import { useEffect, useMemo, useState } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { useSnapshot } from 'valtio';
import { SIGNIFY_RECORD_NAME_DESTINATION, type SignifyMappingsConfig } from '#types';
import { createDocumentStore } from '../../store';

type FirestoreSignifyMappings = SignifyMappingsConfig & {
  lastModified?: unknown;
  modifiedBy?: string;
  docId?: string;
};

const signifyMappingsStore = createDocumentStore<FirestoreSignifyMappings>('parameters/signifyMappings');

export const useSignifyMappings = () => {
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setError(null);
    signifyMappingsStore.connect({ onError: (e) => setError(e) });
    return () => signifyMappingsStore.disconnect();
  }, []);

  const snap = useSnapshot(signifyMappingsStore.state);
  const data = snap.data as FirestoreSignifyMappings | null;
  const loading = snap.loading;

  const mappings: SignifyMappingsConfig = useMemo(() => ({
    fieldMappings: data?.fieldMappings ?? {},
    manufacturerMappings: data?.manufacturerMappings ?? {},
  }), [data]);

  const configDisabledReason = useMemo<string | null>(() => {
    if (loading) return null;
    if (Object.keys(mappings.fieldMappings).length === 0) {
      return 'Signify field mappings are not configured. Add Field Mappings on the Signify mappings settings page.';
    }
    if ((mappings.fieldMappings[SIGNIFY_RECORD_NAME_DESTINATION] ?? []).length === 0) {
      return 'Signify record naming is not configured. Add rules for the Record Name destination on the Signify mappings settings page.';
    }
    return null;
  }, [loading, mappings]);

  const save = async (config: SignifyMappingsConfig) => {
    await signifyMappingsStore.set(
      { ...config, lastModified: serverTimestamp() },
      { debounce: false }
    );
  };

  return { mappings, loading, error, configDisabledReason, save };
};
